<template>
    <div class="d-flex ml-auto layers-header px-2 py-4 align-items-center justify-content-between">
        <div class="d-flex align-items-center">
            <span>{{ title }}</span>
            <div v-if="isPreviewStep" class="ml-2">
                <layers-header-list-preview />
            </div>
        </div>
        <div v-if="!isReadonly" class="d-flex align-items-center">
            <template v-if="canvasElements && canvasElements.length">
                <c-button
                    class="btn"
                    size="icon"
                    title="Find in Layers"
                    variant="light"
                    :icon="['fas', 'search']"
                    @click.stop="emit('open-find-layers')"
                />
                <button
                    v-if="!areAllElementsUnSelected && !areAllElementsSelected"
                    class="btn btn-link"
                    @click.stop="selectAllUnlockedElements"
                >
                    Select unlocked
                </button>
                <button
                    v-if="areAllElementsUnSelected && !areAllElementsSelected"
                    class="btn btn-link"
                    @click.stop="selectAllElements"
                >
                    Select all
                </button>
                <button v-if="areAllElementsSelected" class="btn btn-link" @click.stop="clearSelection">
                    Clear selection
                </button>
                <div class="border mx-2" />
            </template>
            <template v-if="getActiveLayers && getActiveLayers.length">
                <div v-if="areAllSelectedElementsUnlocked" class="d-flex align-items-center" @click="toggleAllVisible">
                    <font-awesome-icon
                        v-if="areAllSelectedElementsHidden"
                        v-tippy
                        content="View selected"
                        class="mr-1 actionable-icon"
                        :icon="['far', 'eye']"
                        data-dd-action-name="Hide selected"
                    />
                    <font-awesome-icon
                        v-else-if="areAllSelectedElementsVisible"
                        v-tippy
                        class="mr-1 actionable-icon"
                        content="Hide selected"
                        :icon="['far', 'eye-slash']"
                        data-dd-action-name="View selected"
                    />
                </div>
                <div class="d-flex align-items-center" @click="toggleAllLock">
                    <font-awesome-icon
                        v-if="areAllSelectedElementsUnlocked"
                        v-tippy
                        content="Lock selected"
                        class="mr-1 actionable-icon"
                        :icon="['far', 'lock']"
                        data-dd-action-name="Lock selected"
                    />
                    <font-awesome-icon
                        v-else-if="areAllSelectedElementsLocked"
                        v-tippy
                        class="mr-1 actionable-icon"
                        content="Unlock selected"
                        :icon="['far', 'lock-open']"
                        data-dd-action-name="Unlock selected"
                    />
                </div>
            </template>
            <template v-if="flags?.isAbleToGroupOrUnGroup">
                <div v-if="flags?.showGroups" class="layout-btns-container">
                    <button v-if="showGroupButton" class="btn btn-link primary" @click.stop="groupSelectedItems()">
                        Create Layout
                    </button>
                    <button v-if="showUnGroupButton" class="btn btn-link primary" @click.stop="unGroupSelectedItems()">
                        Remove Layout
                    </button>
                </div>
                <div
                    v-else
                    v-tippy
                    class="ml-3 heading-section d-flex"
                    content="Your current plan doesn't include this feature. Please check your upgrade options with our supoport team :)"
                >
                    <a v-if="showGroupButton" class="disabled">Create Layout</a>
                </div>
            </template>

            <c-dropdown
                v-if="areAllElementsUnSelected && flags?.enableImportLayers"
                label="Import"
                variant="empty"
                :items="importFromOptions"
            >
                <template #dropdown-menu-item="{ thumb, label }">
                    <div class="d-flex align-items-center w-100">
                        <div v-if="thumb" class="d-flex align-items-center mr-2">
                            <svg-icon :src="thumb" />
                        </div>
                        <span class="w-100">{{ label }}</span>
                    </div>
                </template>
            </c-dropdown>

            <modal-import
                :is-open="isModalImportOpen"
                :pre-selected-tab="preSelectedTab"
                :handle-close="closeModalImport"
                :handle-open="openModalImport"
                @import-layers="importLayers"
            />
            <input id="importFromFile" type="file" accept=".psd" value="upload" hidden @change="uploadPsdFile" />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import store from '@/store';
import SvgIcon from '@/components/SvgIcon/SvgIcon.vue';
import CDropdown from '@/components/shared/CDropdown/CDropdown.vue';
import ModalImport from '@/components/Layers/modal/import/ModalImport.vue';

import { canGroupSelectedItems, canUnGroupSelectedItems } from '@/utils/layers/groupUtils';
import templateSvg from '@/assets/images/template.svg';
import canvaSvg from '@/assets/images/canva-logo.svg';
import psdSvg from '@/assets/images/photoshop.svg';
import { useFileLayers } from '@/composables/externalFileParser';
import { CDropdownItem } from '@/components/shared/CDropdown';
import { ImportLayersEventData } from '@/typings/layers/layers';
import { Tabs } from '../modal/types';
import { useCanva } from '@/composables/canva';
import CButton from '@/components/shared/CButton/CButton.vue';
import LayersHeaderListPreview from '@/components/Layers/header/list-preview/LayersHeaderListPreview.vue';

const { data: psdLayers, parseAndExtractLayers } = useFileLayers('PSD');
const { linkAccountToCanva } = useCanva(
    () => emit('user-updated'),
    () => preSelectTab(Tabs.Canva)
);

const showLoader = ref(false);
const isModalImportOpen = ref(false);
const preSelectedTab = ref<Tabs | undefined>(undefined);
const importFromOptions: CDropdownItem[] = [
    {
        label: 'Layers from Design/Template',
        thumb: templateSvg,
        callback: () => preSelectTab(Tabs.Templates)
    },
    {
        label: 'PSD File',
        thumb: psdSvg,
        divider: true,
        callback: () => fireInputPsdFileClick()
    },
    {
        label: 'From Canva',
        thumb: canvaSvg,
        divider: true,
        callback: () => linkAccountToCanva()
    }
];
const props = defineProps<{
    startWithCanva?: boolean;
    title?: string;
}>();

const emit = defineEmits(['import-layers', 'user-updated', 'open-find-layers']);

watch(
    () => [store.state.user, props.startWithCanva],
    async ([user, startWithCanva]) => {
        if (!user || !startWithCanva) return;
        await linkAccountToCanva();
    },
    { immediate: true }
);

const canvasElements = computed(() => {
    return store.state.storeLayers?.rawElements.canvasElements;
});

const flags = computed(() => {
    return store.state.storeLayers?.flags;
});

const isPreviewStep = computed(() => {
    return flags.value?.isPreviewStep;
});

const isReadonly = computed(() => {
    return flags.value?.readonly;
});

const areAllElementsSelected = computed(() => {
    return store.getters['storeLayers/areAllElementsSelected'];
});

const areAllElementsUnSelected = computed(() => {
    return store.getters['storeLayers/areAllElementsUnSelected'];
});

const getActiveLayers = computed(() => {
    return store.getters['storeLayers/getAllSelectedCanvasElements'];
});

const areAllSelectedElementsUnlocked = computed(() => {
    return store.getters['storeLayers/areAllSelectedElementsUnlocked'];
});

const areAllSelectedElementsLocked = computed(() => {
    return store.getters['storeLayers/areAllSelectedElementsLocked'];
});

const areAllSelectedElementsHidden = computed(() => {
    return store.getters['storeLayers/areAllSelectedElementsHidden'];
});

const areAllSelectedElementsVisible = computed(() => {
    return store.getters['storeLayers/areAllSelectedElementsVisible'];
});

const showGroupButton = computed(() => {
    if (!store.state.storeLayers?.canvas) {
        return false;
    }
    return canGroupSelectedItems(store.state.storeLayers?.canvas);
});

const showUnGroupButton = computed(() => {
    if (!store.state.storeLayers?.canvas) {
        return false;
    }
    return canUnGroupSelectedItems(store.state.storeLayers?.canvas);
});

const importLayers = (data: ImportLayersEventData) => {
    const { handleClose, handleError, preserveId, preserveGroups, importedFileName } = data;
    emit('import-layers', {
        preserveId,
        preserveGroups,
        importedFileName: importedFileName,
        asPng: data.asPng,
        from: data.from,
        canvasElements: data.canvasElements,
        canvasGroups: data.canvasGroups,
        fileElements: data.fileElements,
        fileDimensions: data.fileDimensions,
        pageBackgroundColor: data.pageBackgroundColor,
        errorMessage: data.errorMessage,
        notFoundFonts: data.notFoundFonts,
        handleClose,
        handleError
    });
};

const selectAllUnlockedElements = (): void => {
    store.dispatch('storeLayers/selectAllUnlockedElements');
};

const selectAllElements = (): void => {
    store.dispatch('storeLayers/selectAllElements');
};

const clearSelection = (): void => {
    store.dispatch('storeLayers/clearSelectedItems');
    store.state.storeLayers?.canvas?.discardActiveObject();
};

const toggleAllVisible = (): void => {
    store.dispatch('storeLayers/toggleAllVisible');
};

const toggleAllLock = (): void => {
    store.dispatch('storeLayers/toggleAllLock');
};

const groupSelectedItems = (): void => {
    try {
        const { canvas } = store.state.storeLayers || {};
        canvas?.groupSelectedItems();
    } catch (error) {
        console.error('Error grouping selected elements:', error);
    }
};

const unGroupSelectedItems = (): void => {
    try {
        const { canvas } = store.state.storeLayers || {};
        canvas?.unGroupSelectedItems();
    } catch (error) {
        console.error('Error ungrouping selected elements:', error);
    }
};

const openModalImport = (): void => {
    isModalImportOpen.value = true;
};

const closeModalImport = (): void => {
    isModalImportOpen.value = false;
    preSelectedTab.value = undefined;
};

const preSelectTab = (tab: Tabs) => {
    openModalImport();
    preSelectedTab.value = tab;
};

const fireInputPsdFileClick = () => {
    const input = document.getElementById('importFromFile') as HTMLInputElement;
    if (!input) return;
    input.click();
};

const uploadPsdFile = async (event: Event) => {
    try {
        showLoader.value = true;
        const { files } = event.target as HTMLInputElement;
        if (!(files && files[0])) return;
        const file = files[0];
        if (file.type !== 'image/vnd.adobe.photoshop' && !file.name.endsWith('.psd')) {
            console.error('Invalid file type. Please select a PSD file.');
            return;
        }
        const { name } = file;
        const buffer = await file.arrayBuffer();
        await parseAndExtractLayers(buffer);
        store.dispatch('storeLayersModal/setParsedFileLayers', {
            ...psdLayers.value,
            artworkName: name,
            selectedPage: 1
        });
        preSelectTab(Tabs.ExternalLayers);
    } catch (error) {
        console.log('error', error);
    } finally {
        showLoader.value = false;
    }
};
</script>

<style lang="scss" scoped>
.layers-header {
    --primary-blue: #87add5;
    --secondary-color: #a7aab4;
    --secondary-hover-color: #868686;
    height: 32px;
    min-height: 32px;
    display: flex;
    align-items: center;
    width: 100%;

    background-color: #f5f5f5;
    border-bottom: 1px solid #ddd;

    .actionable-icon {
        cursor: pointer;
        color: var(--secondary-hover-color);
        font-size: 1rem;
        margin-left: 1rem;
    }

    .btn-link {
        box-shadow: none !important;
        color: var(--secondary-color);
        font-size: 0.75rem;
        font-weight: 600;
        padding-top: 0;
        padding-bottom: 0;
        line-height: 1;

        &:hover {
            color: var(--secondary-hover-color);
        }

        &.primary,
        &.primary:hover {
            padding-right: 0;
            color: var(--primary-blue);
            font-size: 0.875rem;
        }
    }
    .layout-btns-container {
        display: flex;
        align-items: center;
        margin-top: 0;
    }
}
</style>
